import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { auth, db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useWindowSize } from 'react-use';
import Confetti from 'react-confetti';

function Step1() {
  const { state, dispatch } = useApp();
  const { appData, showConfetti } = state;
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: appData.name || '',
    url: appData.url || '',
    category: appData.category || ''
  });
  const [urlError, setUrlError] = useState(''); // Estado para mensagens de erro
  const { width, height } = useWindowSize();

  // ✅ Controlar confetti via context
  useEffect(() => {
    if (showConfetti) {
      // Parar confetti após 5 segundos
      const timer = setTimeout(() => {
        dispatch({ type: 'SET_CONFETTI', payload: false });
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showConfetti, dispatch]);

  useEffect(() => {
    const fetchUserCategory = async (user) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const category = userData.category || 'general';
          
          setFormData(prev => ({
            ...prev,
            category: category
          }));

          dispatch({ 
            type: 'UPDATE_APP_DATA', 
            payload: { category: category } 
          });
        }
      } catch (error) {
        console.error('Erro ao buscar categoria:', error);
        setFormData(prev => ({
          ...prev,
          category: 'general'
        }));
      } finally {
        setLoading(false);
      }
    };

    // ✅ Usar observer para garantir autenticação
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchUserCategory(user);
      } else {
        setLoading(false);
        setFormData(prev => ({ ...prev, category: 'general' }));
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  // Função para validar o formato básico da URL
  const isValidUrlFormat = (urlString) => {
    try {
      new URL(urlString);
      return true;
    } catch (_) {
      return false;
    }
  };

  // Função para verificar se o domínio existe (versão simplificada)
  const checkDomainExists = async (domain) => {
    try {
      // Esta é uma verificação básica. Para uma validação mais robusta,
      // você precisaria implementar uma verificação de DNS no backend.
      const response = await fetch(`https://dns.google/resolve?name=${domain}&type=A`);
      const data = await response.json();
      return data.Status === 0; // Status 0 indica que a consulta foi bem-sucedida
    } catch (error) {
      console.error('Erro ao verificar domínio:', error);
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validar URL em tempo real (opcional)
    if (name === 'url' && value.trim() !== '') {
      if (!isValidUrlFormat(value)) {
        setUrlError('Formato de URL inválido. Use: https://exemplo.com');
      } else {
        setUrlError('');
      }
    } else if (name === 'url') {
      setUrlError('');
    }
  };

  const handleNext = async () => {
    // ✅ Validação adicional
    if (!formData.name.trim() || !formData.url.trim()) {
      alert('Preencha todos os campos obrigatórios');
      return;
    }

    // Validar formato da URL antes de prosseguir
    if (!isValidUrlFormat(formData.url)) {
      setUrlError('Formato de URL inválido. Use: https://exemplo.com');
      return;
    }

    // Extrair domínio para verificação
    const domain = new URL(formData.url).hostname;
    
    // Verificar se o domínio existe (pode ser demorado)
    setLoading(true);
    const domainExists = await checkDomainExists(domain);
    setLoading(false);

    if (!domainExists) {
      setUrlError('Domínio não encontrado. Verifique se a URL está correta.');
      return;
    }

    // Se todas as validações passarem, prosseguir
    dispatch({ type: 'UPDATE_APP_DATA', payload: formData });
    // ✅ Preço base já foi carregado do Firestore em AppCreation.js - não sobrescrever aqui
    dispatch({ type: 'NEXT_STEP' });
  };

  const categories = [
    { value: 'business', label: 'Negócios' },
    { value: 'education', label: 'Educação' },
    { value: 'entertainment', label: 'Entretenimento' },
    { value: 'food', label: 'Alimentação' },
    { value: 'health', label: 'Saúde' },
    { value: 'lifestyle', label: 'Estilo de Vida' },
    { value: 'news', label: 'Notícias' },
    { value: 'shopping', label: 'Compras' },
    { value: 'social', label: 'Social' },
    { value: 'sports', label: 'Esportes' },
    { value: 'travel', label: 'Viagem' },
    { value: 'general', label: 'Geral' }
  ];

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Confetti de boas-vindas */}
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={500}
        />
      )}
      
      <div className="bg-white p-8 rounded-2xl shadow-xl">
        <h2 className="text-3xl font-bold mb-8 text-gray-900">Nome e URL do aplicativo</h2>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Nome do aplicativo *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-200 text-gray-900"
            placeholder="Digite o nome do seu aplicativo"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            URL personalizada *
          </label>
          <div className="flex">
            <input
              type="text"
              name="url"
              value={formData.url}
              onChange={handleChange}
              className={`flex-1 min-w-0 block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-200 text-gray-900 ${
                urlError ? 'ring-2 ring-red-500 bg-red-50' : ''
              }`}
              placeholder="https://exemplo.com"
              required
            />
          </div>
          {urlError && (
            <p className="mt-2 text-sm text-red-600 font-medium">{urlError}</p>
          )}
          <p className="mt-2 text-sm text-gray-600">
            Nota: A URL não pode ser alterada posteriormente sem custos adicionais.
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Categoria do aplicativo *
          </label>
          <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
            <p className="font-medium text-gray-900">
              {categories.find(cat => cat.value === formData.category)?.label || 'Geral'}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              Categoria definida no seu perfil de usuário
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10 flex justify-end">
        <button
          onClick={handleNext}
          disabled={!formData.name.trim() || !formData.url.trim() || urlError}
          className="bg-gray-900 text-white px-8 py-3 rounded-xl hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          Próximo
        </button>
      </div>
    </div>
    </>
  );
}

export default Step1;
